// orderPurchaseTracking.js
// The order's own `purchase` pixel event — funnel_step 15.
//
// This used to fire in exactly one place: WeightLossAfterCheckout.mounted(), reading
// the amount and transaction id back out of the URL. That placement left three holes.
//
//   • Klarna never reported a purchase at all. That leg confirms on KlarnaReturn and
//     routes to verifyPhoneAndEmail, which has no tracking and then sends the
//     customer to the homepage — the thanks page is never reached.
//   • The call-center checkout never reported one either. It routes to the thanks
//     page with `amount` but no `trans_id`, and the event bails when either is
//     missing.
//   • Refreshing the thanks page reported the same order again, because both values
//     come from the query string.
//
// It now fires where the server confirms the payment. The thanks page still calls it,
// so confirmation links opened later keep reporting, and a transaction that has
// already been reported is skipped — that stamp is what makes both call sites safe.
//
// The add-on sold in the post-purchase modal is a SEPARATE purchase on its own
// funnel_step; see fastTrackUpsell.js. Its fee is never folded into this event.

import { newEventId } from '@/utils/trackingIds';

/** The order's step. The add-on's purchase is 16 — see fastTrackUpsell.js. */
export const ORDER_PURCHASE_FUNNEL_STEP = 15;

const FIRED_KEY = 'tracking_purchase_fired';

/**
 * The same stamp, held in memory.
 *
 * The localStorage write is best-effort: a browser with storage blocked, or a full
 * quota, throws on setItem and the stamp never lands. Every checkout reaches the
 * thanks page with `this.$router.push`, so this module is never re-evaluated and
 * this set still holds — which is what stops that page reporting the order a second
 * time when the stamp could not be written.
 */
const firedThisSession = new Set();

/**
 * The product name both older checkouts write before payment. Read only when the
 * funnel left no `tracking_checkout` record — a call-center customer opens the
 * payment link cold, so nothing upstream ever wrote one.
 */
const storedProductName = () => {
    try {
        const raw = localStorage.getItem('productName');
        if (!raw) return '';
        const parsed = JSON.parse(raw);
        return typeof parsed === 'string' ? parsed : '';
    } catch (error) {
        return '';
    }
};

/**
 * How many transaction ids to remember. Enough that reopening a recent confirmation
 * link cannot re-report the order, without growing unbounded.
 */
const FIRED_KEEP = 10;

const readFiredIds = () => {
    try {
        const parsed = JSON.parse(localStorage.getItem(FIRED_KEY) || '[]');
        return Array.isArray(parsed) ? parsed : [];
    } catch (error) {
        return [];
    }
};

/** True once this transaction's purchase has been reported. */
export const hasOrderPurchaseFired = (transactionId) => {
    if (!transactionId) return false;
    if (firedThisSession.has(String(transactionId))) return true;
    return readFiredIds().indexOf(String(transactionId)) !== -1;
};

const markOrderPurchaseFired = (transactionId) => {
    // In memory first, and never inside the try: this is the half that has to
    // survive a storage failure, because it is the half the thanks page checks
    // one router push later.
    firedThisSession.add(String(transactionId));
    try {
        const ids = readFiredIds().filter((id) => id !== String(transactionId));
        ids.push(String(transactionId));
        localStorage.setItem(FIRED_KEY, JSON.stringify(ids.slice(-FIRED_KEEP)));
    } catch (error) {
        // Storage blocked. A confirmation link opened in a new tab can still
        // report this order again; the set above covers the common case.
        console.warn('[tracking] purchase stamp could not be written:', error);
    }
};

/** How long to wait for an async pixel before giving the order up as unreportable. */
const PIXEL_WAIT_MS = 8000;
const PIXEL_POLL_MS = 100;

/** Transactions already waiting on the pixel, so two callers cannot queue two waits. */
const awaitingPixel = new Set();

/**
 * Hold the order until the pixel script finishes loading, then report it.
 *
 * Only reached when `window.tracking` is missing at call time. Every exit path either
 * fires once through the normal function (which stamps the transaction) or stops, so
 * this cannot produce a second event for an order that already reported.
 */
const waitForPixel = ({ amount, transactionId }) => {
    const key = String(transactionId);
    if (awaitingPixel.has(key)) return;
    awaitingPixel.add(key);

    const deadline = Date.now() + PIXEL_WAIT_MS;
    const timer = setInterval(() => {
        if (hasOrderPurchaseFired(transactionId)) {
            clearInterval(timer);
            awaitingPixel.delete(key);
            return;
        }
        if (window.tracking && window.tracking.event) {
            clearInterval(timer);
            awaitingPixel.delete(key);
            fireOrderPurchase({ amount, transactionId });
            return;
        }
        if (Date.now() > deadline) {
            clearInterval(timer);
            awaitingPixel.delete(key);
            console.warn('[tracking] pixel never loaded; order not reported:', key);
        }
    }, PIXEL_POLL_MS);
};

/**
 * Report the order. Returns true when the event was sent.
 *
 * Never throws. The customer has already paid by the time this runs, so a tracking
 * failure must not interrupt them or block the confirmation redirect.
 *
 * Product details, currency, form and event_id come from the `tracking_checkout`
 * payload the funnel wrote at add_to_cart — the same source the thanks page read, so
 * the event is identical wherever it fires from.
 */
export const fireOrderPurchase = ({ amount, transactionId }) => {
    try {
        // The guard the thanks page has always had: without both of these the event
        // cannot be attributed, so it is not worth sending.
        if (!amount || !transactionId) return false;
        if (hasOrderPurchaseFired(transactionId)) return false;
        if (typeof window === 'undefined') return false;

        // The pixel script is loaded with `async`, so it can still be in flight when
        // this runs. The thanks page calls this from mounted(), which lost the event
        // outright on a slow pixel — and for a confirmation link opened later, that
        // page is the only thing reporting the order. Wait for it instead of giving up.
        if (!window.tracking || !window.tracking.event) {
            waitForPixel({ amount, transactionId });
            return false;
        }

        let checkout = {};
        try {
            checkout = JSON.parse(localStorage.getItem('tracking_checkout') || '{}') || {};
        } catch (error) {
            console.warn('[tracking] tracking_checkout read failed:', error);
        }

        window.tracking.event('purchase', {
            funnel_step: ORDER_PURCHASE_FUNNEL_STEP,
            form: checkout.form || 'home',
            event_value: parseFloat(amount),
            currency: checkout.currency || 'USD',
            transaction_id: String(transactionId),
            // An empty event_id cannot be deduplicated against the server's copy of
            // this purchase, so mint one when the funnel left none. Call-center and
            // Klarna customers open the payment link cold and were reporting blank.
            event_id: checkout.event_id || newEventId(),
            product_id: checkout.product_id || '',
            product_name: checkout.product_name || storedProductName(),
            quantity: 1,
        });

        markOrderPurchaseFired(transactionId);
        try {
            localStorage.removeItem('tracking_checkout');
        } catch (error) {
            console.warn('[tracking] tracking_checkout could not be cleared:', error);
        }
        return true;
    } catch (error) {
        console.error('[tracking] order purchase tracking failed:', error);
        return false;
    }
};
